export type ConnectionStatus = 'ok' | 'localSlow' | 'opponentOffline'

/** A heartbeat round-trip unanswered for longer than this => your link is slow. */
export const LOCAL_SLOW_MS = 6_000
/** Opponent heartbeat older than this (server clock) => treat as offline. */
export const OPPONENT_OFFLINE_MS = 20_000

export interface ConnectionInputs {
  /** Local wall-clock "now" (ms). */
  now: number
  /**
   * Local time the oldest still-unanswered heartbeat round-trip began, or null
   * when the most recent heartbeat has been echoed back by the backend.
   */
  pendingPingSince: number | null
  /** Skew-free "server now" estimate (ms), or null if not yet known. */
  serverNowMs: number | null
  /** Opponent's last heartbeat in server-clock ms, or null if none seen. */
  opponentLastSeenMs: number | null
  /** Whether it is currently the opponent's turn. */
  isOpponentTurn: boolean
}

/**
 * null  -> unknown / connecting (no server-now ref or no opponent heartbeat yet)
 * true  -> opponent heartbeat is recent
 * false -> opponent heartbeat is stale
 */
export function isOpponentOnline(
  serverNowMs: number | null,
  opponentLastSeenMs: number | null,
): boolean | null {
  if (serverNowMs === null || opponentLastSeenMs === null) {
    return null
  }
  return serverNowMs - opponentLastSeenMs <= OPPONENT_OFFLINE_MS
}

export function deriveConnectionStatus(i: ConnectionInputs): ConnectionStatus {
  if (
    i.pendingPingSince !== null &&
    i.now - i.pendingPingSince > LOCAL_SLOW_MS
  ) {
    return 'localSlow'
  }
  if (
    i.isOpponentTurn &&
    isOpponentOnline(i.serverNowMs, i.opponentLastSeenMs) === false
  ) {
    return 'opponentOffline'
  }
  return 'ok'
}

/**
 * Estimate the current server time without trusting the local clock's absolute
 * value: take the last server timestamp we read back from our OWN presence doc
 * and project it forward by the local time elapsed since we recorded it. Both
 * the opponent's lastSeen and this estimate are then on the server clock, so the
 * staleness comparison is skew-free.
 */
export function estimateServerNow(
  ownLastSeenMs: number | null,
  ownRecordedAtLocalMs: number | null,
  nowLocalMs: number,
): number | null {
  if (ownLastSeenMs === null || ownRecordedAtLocalMs === null) {
    return null
  }
  return ownLastSeenMs + (nowLocalMs - ownRecordedAtLocalMs)
}
